/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {InjectionToken} from '@angular/core';
import {LocationChangeListener} from './platform_location';
/**
 * `LocationStrategy` is responsible for representing and reading route state
 * from the browser's URL. Angular provides two strategies:
 * {\@link HashLocationStrategy} and {\@link PathLocationStrategy}.
 * 
 * This is used under the hood of the {\@link Location} service.
 * 
 * Applications should use the {\@link Router} or {\@link Location} services to
 * interact with application route state.
 * 
 * For instance, {\@link HashLocationStrategy} produces URLs like
 * `http://example.com#/foo`, and {\@link PathLocationStrategy} produces
 * `http://example.com/foo` as an equivalent URL.
 * 
 * See these two classes for more.
 * 
 * \@stable
 * @abstract
 */
export abstract class LocationStrategy {
/**
 * @abstract
 * @param {?=} includeHash
 * @return {?}
 */
path(includeHash?: boolean) {}
/**
 * @abstract
 * @param {?} internal
 * @return {?}
 */
prepareExternalUrl(internal: string) {}
/**
 * @abstract
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @param {?} queryParams
 * @return {?}
 */
pushState(state: any, title: string, url: string, queryParams: string) {}
/**
 * @abstract
 * @param {?} state
 * @param {?} title
 * @param {?} url
 * @param {?} queryParams
 * @return {?}
 */
replaceState(state: any, title: string, url: string, queryParams: string) {}
/**
 * @abstract
 * @return {?}
 */
forward() {}
/**
 * @abstract
 * @return {?}
 */
back() {}
/**
 * @abstract
 * @param {?} fn
 * @return {?}
 */
onPopState(fn: LocationChangeListener) {}
/**
 * @abstract
 * @return {?}
 */
getBaseHref() {}
}
/**
 * The `APP_BASE_HREF` token represents the base href to be used with the
 * {\@link PathLocationStrategy}.
 * 
 * If you're using {\@link PathLocationStrategy}, you must provide a provider to a string
 * representing the URL prefix that should be preserved when generating and recognizing
 * URLs.
 * 
 * ### Example
 * 
 * ```typescript
 * import {Component, NgModule} from '\@angular/core';
 * import {APP_BASE_HREF} from '\@angular/common';
 * 
 * \@NgModule({ 
 *   providers: [{provide: APP_BASE_HREF, useValue: '/my/app'}]
 * })
 * class AppModule {}
 * ```
 * 
 * \@stable
 */
export const APP_BASE_HREF = new InjectionToken<string>('appBaseHref');



// WEBPACK FOOTER //
// ../../../../packages/common/src/location/location_strategy.ts